import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  ArrowDown,
  ArrowUp,
  Calendar,
  Check,
  CheckCircle2,
  ChevronRight,
  Edit2,
  Eye,
  EyeOff,
  ExternalLink,
  Inbox,
  Package,
  PackageX,
  Pencil,
  Plus,
  RefreshCw,
  Scissors,
  ShoppingBag,
  Tag,
  TrendingUp,
  Trash2,
  X,
} from "lucide-react";
import { RequireRole } from "@/lib/auth";
import { BackButton } from "@/components/BackButton";
import { EmptyState } from "@/components/EmptyState";
import { Badge, productStatusBadge } from "@/components/Badge";
import { CardImage, CardImageUploader } from "@/components/CardImage";
import { ModalSheet } from "@/components/Modal";
import { deleteImage } from "@/features/imageUpload";
import { BinPickerModal } from "@/components/BinPickerModal";
import {
  FormField,
  MoneyField,
  NumberField,
  DropdownField,
} from "@/components/FormField";
import { SetCombobox } from "@/components/SetCombobox";
import { PrimaryButton } from "@/components/PrimaryButton";
import {
  useApp,
  type Card,
  type CardFinish,
  type CardCondition,
  type CardGame,
  type CardRarity,
  type PriceHistoryEntry,
  type PriceSource,
  type Product,
} from "@/context/AppContext";
import { conditionAdjust, fetchCardMarketPrice } from "@/features/tcgApi";
import { useInventoryCardLookup } from "@/features/useInventoryCardLookup";
import { formatTimeAgo } from "@/features/formatTime";
import { matchPokemonToFamily } from "@/features/pokemonFamilyMatcher";
import {
  fetchEbaySales,
  mergeEbaySalesIntoHistory,
} from "@/features/salesHistoryFetcher";

export const Route = createFileRoute("/inventory/$id")({
  component: InventoryDetailPage,
});

const GAMES: CardGame[] = ["Pokemon", "Magic", "Yu-Gi-Oh", "Other"];
const PRICE_SOURCES: PriceSource[] = [
  "Manual",
  "TCGplayer",
  "eBay Sold",
  "Other",
];
const RARITIES: CardRarity[] = [
  "Common",
  "Uncommon",
  "Rare",
  "Promo",
  "Trainer",
  "Energy",
  "Damaged",
  "Sealed",
];
const CONDITIONS: CardCondition[] = ["NM", "LP", "MP", "HP", "Damaged"];
const LISTING_STATUSES = ["Unlisted", "Listed", "In Package", "Sold"] as const;
const LANGUAGES = [
  "English",
  "Japanese",
  "Chinese",
  "Korean",
  "German",
  "French",
  "Italian",
  "Spanish",
  "Portuguese",
  "Other",
];
const GRADING_COMPANIES = [
  "Not graded",
  "PSA",
  "BGS",
  "CGC",
  "SGC",
  "TAG",
  "Other",
];
// Finish + Energy + Supertype dropdowns: value "" = none, with a friendly label.
const FINISH_OPTIONS = [
  { value: "", label: "Regular" },
  { value: "Holo", label: "Holo" },
  { value: "Reverse Holo", label: "Reverse Holo" },
  { value: "Premium", label: "Premium" },
];
const ENERGY_OPTIONS = [
  { value: "", label: "(none)" },
  ...[
    "Grass",
    "Fire",
    "Water",
    "Lightning",
    "Psychic",
    "Fighting",
    "Darkness",
    "Metal",
    "Fairy",
    "Dragon",
    "Colorless",
  ].map((t) => ({ value: t, label: t })),
];
const SUPERTYPE_OPTIONS = [
  { value: "", label: "(none)" },
  { value: "Pokémon", label: "Pokémon" },
  { value: "Trainer", label: "Trainer" },
  { value: "Energy", label: "Energy" },
];
type ListingStatus = (typeof LISTING_STATUSES)[number];

function InventoryDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const {
    cards,
    lots,
    products,
    deleteCard,
    updateCard,
    addCardsToProduct,
    splitCardStack,
    archiveCard,
    restoreCard,
  } = useApp();
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [addedToBanner, setAddedToBanner] = useState<string | null>(null);
  const addedBannerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (addedBannerTimer.current) clearTimeout(addedBannerTimer.current);
    },
    [],
  );

  // TNK-155 phase B — resolve the card via the lookup hook so a deep
  // link works even when AppContext hasn't loaded the full inventory.
  // Phase D removes the eager `cards` hydration; the hook continues
  // working via its server-fetch fallback.
  const { phase: cardLookupPhase, card } = useInventoryCardLookup(id);

  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const bindField = (key: string) => (el: HTMLDivElement | null) => {
    fieldRefs.current[key] = el;
  };

  // ── Edit state ─────────────────────────────────────────────────────────────
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [game, setGame] = useState<CardGame>("Pokemon");
  const [cardSetName, setCardSetName] = useState("");
  const [setCode, setSetCode] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [rarity, setRarity] = useState<CardRarity>("Rare");
  const [variant, setVariant] = useState("");
  const [condition, setCondition] = useState<CardCondition>("NM");
  const [quantity, setQuantity] = useState("1");
  const [sourceLotId, setSourceLotId] = useState("");
  const [costBasis, setCostBasis] = useState("");
  const [marketPrice, setMarketPrice] = useState("");
  const [expectedSalePrice, setExpectedSalePrice] = useState("");
  const [storageLocation, setStorageLocation] = useState("");
  const [binPickerVisible, setBinPickerVisible] = useState(false);
  const [listingStatus, setListingStatus] = useState<ListingStatus>("Unlisted");
  const [notes, setNotes] = useState("");
  const [illustrator, setIllustrator] = useState("");
  const [energyType, setEnergyType] = useState("");
  const [supertype, setSupertype] = useState("");
  const [stage, setStage] = useState("");
  const [year, setYear] = useState("");
  const [finish, setFinish] = useState<CardFinish | "">("");
  const [language, setLanguage] = useState("English");
  const [gradingCompany, setGradingCompany] = useState("Not graded");
  const [imageFrontRef, setImageFrontRef] = useState<string | undefined>(
    undefined,
  );
  const [imageBackRef, setImageBackRef] = useState<string | undefined>(
    undefined,
  );
  const [costAutoFilled, setCostAutoFilled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState("");
  const [conditionError, setConditionError] = useState("");
  const [quantityError, setQuantityError] = useState("");
  const [costBasisError, setCostBasisError] = useState("");
  const [marketPriceError, setMarketPriceError] = useState("");
  const [expectedSalePriceError, setExpectedSalePriceError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [confirmSplit, setConfirmSplit] = useState(false);
  const [refreshingPrice, setRefreshingPrice] = useState(false);
  const [refreshNotice, setRefreshNotice] = useState<{
    kind: "success" | "info" | "warning";
    text: string;
  } | null>(null);
  useEffect(() => {
    if (!refreshNotice) return;
    const t = setTimeout(() => setRefreshNotice(null), 5000);
    return () => clearTimeout(t);
  }, [refreshNotice]);
  const [showLogPrice, setShowLogPrice] = useState(false);
  const [logPriceValue, setLogPriceValue] = useState("");
  const [logPriceSource, setLogPriceSource] = useState<PriceSource>("Manual");
  const [logPriceDate, setLogPriceDate] = useState("");
  const [logPriceError, setLogPriceError] = useState("");

  const startEditing = () => {
    if (!card) return;
    setName(card.name);
    setGame(card.game);
    setCardSetName(card.setName);
    setSetCode(card.setCode);
    setCardNumber(card.cardNumber);
    setRarity(card.rarity);
    setVariant(card.variant);
    setCondition(card.condition);
    setQuantity(String(card.quantity));
    setSourceLotId(card.sourceLotId);
    setCostBasis(card.costBasis > 0 ? card.costBasis.toFixed(4) : "");
    setMarketPrice(card.marketPrice > 0 ? card.marketPrice.toFixed(2) : "");
    setExpectedSalePrice(
      card.expectedSalePrice > 0 ? card.expectedSalePrice.toFixed(2) : "",
    );
    setStorageLocation(card.storageLocation);
    setListingStatus(card.listingStatus);
    setNotes(card.notes);
    setIllustrator(card.tcgArtist ?? "");
    setEnergyType(card.tcgTypes?.[0] ?? "");
    setSupertype(card.tcgSupertype ?? "");
    setStage(card.tcgSubtypes?.join(", ") ?? "");
    setYear(card.year ?? "");
    setFinish(card.finish ?? "");
    setLanguage(card.language || "English");
    setGradingCompany(
      card.gradingCompany ?? (card.graded ? "Other" : "Not graded"),
    );
    setImageFrontRef(card.imageFrontRef);
    setImageBackRef(card.imageBackRef);
    setCostAutoFilled(false);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setNameError("");
    setConditionError("");
    setQuantityError("");
    setCostBasisError("");
    setMarketPriceError("");
    setExpectedSalePriceError("");
  };

  // ── Lot helpers ────────────────────────────────────────────────────────────
  const getLotStats = (lotId: string) => {
    const lot = lots.find((l) => l.id === lotId);
    if (!lot) return null;
    const totalCost = lot.purchaseCost + lot.shippingCost + lot.taxFees;
    const sortedCount = lot.sortingBins.reduce((s, b) => s + (b.count || 0), 0);
    const totalCards = sortedCount > 0 ? sortedCount : lot.estimatedCards;
    const singlesFromLot = cards.filter((c) => c.sourceLotId === lotId);
    const singlesCount = singlesFromLot.reduce((s, c) => s + c.quantity, 0);
    const costAllocated = singlesFromLot.reduce(
      (s, c) => s + c.costBasis * c.quantity,
      0,
    );
    const perCardCost = totalCards > 0 ? totalCost / totalCards : 0;
    return {
      lot,
      totalCost,
      totalCards,
      singlesCount,
      costAllocated,
      perCardCost,
    };
  };

  const viewLotStats = useMemo(
    () => (card?.sourceLotId ? getLotStats(card.sourceLotId) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [card, lots, cards],
  );

  const editLotStats = useMemo(
    () => (sourceLotId ? getLotStats(sourceLotId) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sourceLotId, lots, cards],
  );

  // Auto-fill cost basis in edit mode when source lot changes.
  useEffect(() => {
    if (!editing) return;
    if (!sourceLotId) {
      if (costAutoFilled) {
        setCostBasis("");
        setCostAutoFilled(false);
      }
      return;
    }
    const stats = getLotStats(sourceLotId);
    if (!stats || stats.perCardCost <= 0) return;
    if (!costBasis || costAutoFilled) {
      setCostBasis(stats.perCardCost.toFixed(4));
      setCostAutoFilled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceLotId, editing]);

  const scrollToFirstError = (errorOrder: string[]) => {
    for (const key of errorOrder) {
      const el = fieldRefs.current[key];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
    }
  };

  const handleSave = () => {
    if (!card) return;
    const errMap: Record<string, boolean> = {};
    if (!name.trim()) {
      setNameError("Enter a card name");
      errMap.name = true;
    } else setNameError("");
    if (!condition) {
      setConditionError("Select a condition");
      errMap.condition = true;
    } else setConditionError("");
    const qty = parseInt(quantity, 10);
    if (!quantity.trim() || isNaN(qty) || qty < 1) {
      setQuantityError("Enter a whole number of 1 or more");
      errMap.quantity = true;
    } else setQuantityError("");
    if (
      costBasis.trim() &&
      (isNaN(parseFloat(costBasis)) || parseFloat(costBasis) < 0)
    ) {
      setCostBasisError("Enter a valid non-negative amount");
      errMap.costBasis = true;
    } else setCostBasisError("");
    if (
      marketPrice.trim() &&
      (isNaN(parseFloat(marketPrice)) || parseFloat(marketPrice) < 0)
    ) {
      setMarketPriceError("Enter a valid non-negative amount");
      errMap.marketPrice = true;
    } else setMarketPriceError("");
    if (
      expectedSalePrice.trim() &&
      (isNaN(parseFloat(expectedSalePrice)) ||
        parseFloat(expectedSalePrice) < 0)
    ) {
      setExpectedSalePriceError("Enter a valid non-negative amount");
      errMap.expectedSalePrice = true;
    } else setExpectedSalePriceError("");

    if (Object.keys(errMap).length > 0) {
      scrollToFirstError(Object.keys(errMap));
      return;
    }

    setSaving(true);
    const subtypeList = stage
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const updated: Card = {
      ...card,
      name: name.trim(),
      game,
      setName: cardSetName,
      setCode,
      cardNumber,
      rarity,
      variant: variant.trim(),
      condition,
      quantity: parseInt(quantity, 10) || 1,
      sourceLotId,
      costBasis: parseFloat(costBasis) || 0,
      marketPrice: parseFloat(marketPrice) || 0,
      expectedSalePrice: parseFloat(expectedSalePrice) || 0,
      storageLocation,
      listingStatus,
      notes,
      // Optional eBay/pokemontcg.io enrichment — strip empty values so we
      // don't write back undefined-marker fields when the seller leaves an
      // input blank.
      tcgArtist: illustrator.trim() || undefined,
      tcgTypes: energyType.trim() ? [energyType.trim()] : undefined,
      tcgSupertype: supertype.trim() || undefined,
      tcgSubtypes: subtypeList.length > 0 ? subtypeList : undefined,
      year: year.trim() || undefined,
      finish: finish || undefined,
      language: language === "English" ? undefined : language,
      graded: gradingCompany !== "Not graded" || undefined,
      gradingCompany:
        gradingCompany === "Not graded" ? undefined : gradingCompany,
      imageFrontRef,
      imageBackRef,
    };
    updateCard(updated);
    setSaving(false);
    setEditing(false);
  };

  const handleConfirmDelete = () => {
    if (!card) return;
    // Best-effort blob cleanup so deleted cards don't leave orphan files on
    // the image volume. Failures are silent — the card removal isn't blocked
    // on the storage call succeeding.
    if (card.imageFrontRef) void deleteImage(card.imageFrontRef);
    if (card.imageBackRef) void deleteImage(card.imageBackRef);
    deleteCard(card.id);
    navigate({ to: "/inventory" });
  };

  const cycleStatus = () => {
    if (!card) return;
    const next =
      card.listingStatus === "Unlisted"
        ? "Listed"
        : card.listingStatus === "Listed"
          ? "Sold"
          : "Unlisted";
    updateCard({ ...card, listingStatus: next });
  };

  const handleRefreshPrice = async () => {
    if (!card || refreshingPrice) return;
    setRefreshingPrice(true);
    setRefreshNotice(null);
    try {
      // Run both fetches in parallel: TCG for current market price, eBay for
      // real sold-listing history. Either failing is fine — we use whatever
      // we got. eBay sales also drive marketPrice if TCG returns nothing.
      const [tcgPriceResult, ebayResult] = await Promise.allSettled([
        fetchCardMarketPrice(card.setCode, card.cardNumber, card.name),
        fetchEbaySales(card),
      ]);

      const tcgPrice =
        tcgPriceResult.status === "fulfilled" ? tcgPriceResult.value : null;
      const ebay =
        ebayResult.status === "fulfilled"
          ? ebayResult.value
          : { median: null, sales: [] };

      const nextMarketPrice = tcgPrice ?? ebay.median;
      const nextSource: PriceSource | undefined =
        tcgPrice != null
          ? "TCGplayer"
          : ebay.median != null
            ? "eBay Sold"
            : undefined;

      // Merge eBay sales into history regardless of which source priced the card
      // — even if TCG won pricing, recent eBay comps are still useful history.
      const prevHistoryLen = card.priceHistory?.length ?? 0;
      const mergedHistory = mergeEbaySalesIntoHistory(
        card.priceHistory ?? [],
        ebay.sales,
      );
      const newSalesCount = Math.max(0, mergedHistory.length - prevHistoryLen);

      if (nextMarketPrice == null && newSalesCount === 0) {
        // Nothing fetched — leave the card untouched but tell the user
        // explicitly rather than silently doing nothing.
        setRefreshNotice({
          kind: "warning",
          text: "No recent eBay sales found for this card.",
        });
        return;
      }

      updateCard({
        ...card,
        marketPrice: nextMarketPrice ?? card.marketPrice,
        expectedSalePrice:
          nextMarketPrice != null
            ? conditionAdjust(nextMarketPrice, card.condition)
            : card.expectedSalePrice,
        priceSource: nextSource ?? card.priceSource,
        pricedAt:
          nextMarketPrice != null ? new Date().toISOString() : card.pricedAt,
        priceHistory: mergedHistory,
      });

      const bits: string[] = [];
      if (tcgPrice != null)
        bits.push(`market $${tcgPrice.toFixed(2)} from TCGplayer`);
      else if (ebay.median != null)
        bits.push(`market $${ebay.median.toFixed(2)} from eBay median`);
      if (newSalesCount > 0)
        bits.push(`${newSalesCount} new sale${newSalesCount === 1 ? "" : "s"}`);
      setRefreshNotice({
        kind: "success",
        text: bits.length > 0 ? `Updated · ${bits.join(" · ")}` : "Up to date.",
      });
    } finally {
      setRefreshingPrice(false);
    }
  };

  const handleLogPrice = () => {
    if (!card) return;
    const parsed = parseFloat(logPriceValue);
    if (!logPriceValue.trim() || isNaN(parsed) || parsed < 0) {
      setLogPriceError("Enter a valid price");
      return;
    }
    if (logPriceDate.trim() && isNaN(new Date(logPriceDate.trim()).getTime())) {
      setLogPriceError("Enter a valid date (e.g. 2025-04-30)");
      return;
    }
    const dateStr = logPriceDate.trim()
      ? new Date(logPriceDate.trim()).toISOString()
      : new Date().toISOString();
    const entry: PriceHistoryEntry = {
      price: parsed,
      source: logPriceSource,
      recordedAt: dateStr,
    };
    const prevHistory: PriceHistoryEntry[] = card.priceHistory ?? [];
    const newHistory = [...prevHistory, entry].slice(-10);
    updateCard({ ...card, priceHistory: newHistory });
    setShowLogPrice(false);
    setLogPriceValue("");
    setLogPriceSource("Manual");
    setLogPriceDate("");
    setLogPriceError("");
  };

  // Memoized lot options derived from lots — must be called BEFORE the
  // `if (!card)` early return below so the hook count is stable across
  // renders (React #310 fires otherwise when `cards` loads in two passes,
  // e.g. after an image rotate + save + reload).
  const lotSelectOptions = useMemo(
    () => [
      { value: "", label: "— No lot —" },
      ...[...lots]
        .sort(
          (a, b) =>
            new Date(b.purchaseDate).getTime() -
            new Date(a.purchaseDate).getTime(),
        )
        .map((l) => {
          const date = new Date(l.purchaseDate).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
          return {
            value: l.id,
            label: l.seller ? `${l.seller} · ${date}` : date,
          };
        }),
    ],
    [lots],
  );

  if (!card) {
    if (cardLookupPhase === "loading") {
      return (
        <div className="flex flex-1 items-center justify-center text-muted-foreground">
          <RefreshCw className="h-5 w-5 animate-spin" />
        </div>
      );
    }
    if (cardLookupPhase === "error") {
      return (
        <EmptyState
          icon={<AlertTriangle className="h-7 w-7" />}
          title="Couldn't load this card"
          description="Connection or server hiccup. Most of these clear up with a quick refresh."
          actionLabel="Refresh"
          onAction={() => window.location.reload()}
        />
      );
    }
    return (
      <EmptyState
        icon={<PackageX className="h-7 w-7" />}
        title="Card not found"
        description={`The id "${id}" doesn't match a card in your inventory. It may have been deleted, or the link could be off by a character — ids look like SKU-…`}
        actionLabel="Back to inventory"
        onAction={() => navigate({ to: "/inventory" })}
      />
    );
  }

  const profit = card.expectedSalePrice - card.costBasis;
  const roi = card.costBasis > 0 ? (profit / card.costBasis) * 100 : 0;
  const lotOptions = lots.map((l) => l.id);

  const family =
    card.game === "Pokemon" ? matchPokemonToFamily(card.name) : null;

  const suggestedCost = viewLotStats?.perCardCost ?? null;
  const costDiff =
    suggestedCost != null && card.costBasis > 0
      ? card.costBasis - suggestedCost
      : null;

  const handleOpenEbay = () => {
    if (card.ebayListingUrl) {
      window.open(card.ebayListingUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="space-y-3.5 pb-12">
      <BackButton fallback={{ to: "/inventory" }} variant="wide" />

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 space-y-1">
          <div className="text-[10px] font-medium tracking-wide text-muted-foreground">
            {card.id}
          </div>
          <div className="text-2xl font-bold text-foreground">{card.name}</div>
          <div className="text-[13px] text-muted-foreground">
            {card.setName}
            {card.cardNumber ? ` · #${card.cardNumber}` : ""}
          </div>
          <div className="flex flex-wrap gap-1.5 pt-1">
            <Badge
              label={card.condition}
              variant={
                card.condition === "NM"
                  ? "success"
                  : card.condition === "Damaged"
                    ? "danger"
                    : "default"
              }
            />
            {card.variant.trim() && (
              <Badge label={card.variant.trim()} variant="accent" />
            )}
            <Badge label={card.rarity} variant="muted" />
            <Badge label={card.game} variant="muted" />
            {card.listingStatus !== "Unlisted" && (
              <Badge
                label={card.listingStatus}
                variant={card.listingStatus === "Sold" ? "success" : "default"}
              />
            )}
            {family && (
              <button
                type="button"
                onClick={() =>
                  navigate({
                    to: "/inventory",
                    search: { family: family.familyId },
                  })
                }
                aria-label={`Filter inventory by ${family.label}`}
                className="cursor-pointer"
              >
                <Badge label={`Family: ${family.label}`} variant="accent" />
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-2 pt-0.5">
          {!editing && (
            <button
              type="button"
              onClick={startEditing}
              className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary/20 text-secondary-foreground hover:bg-secondary/30"
              aria-label="Edit card"
              title="Edit card"
            >
              <Edit2 className="h-[15px] w-[15px]" />
            </button>
          )}
          {/* TNK-212 — per-card storefront hide override (manager+). */}
          <RequireRole min="manager">
            {!editing && (
              <button
                type="button"
                onClick={() =>
                  updateCard({
                    ...card,
                    storefrontHidden: !card.storefrontHidden,
                  })
                }
                className={
                  card.storefrontHidden
                    ? "flex h-9 w-9 items-center justify-center rounded-md bg-muted text-foreground hover:bg-muted/80"
                    : "flex h-9 w-9 items-center justify-center rounded-md bg-secondary/20 text-secondary-foreground hover:bg-secondary/30"
                }
                aria-label={
                  card.storefrontHidden
                    ? "Show in storefront"
                    : "Hide from storefront"
                }
                title={
                  card.storefrontHidden
                    ? "Hidden from storefront — click to show"
                    : "Hide from storefront"
                }
              >
                {card.storefrontHidden ? (
                  <EyeOff className="h-[15px] w-[15px]" />
                ) : (
                  <Eye className="h-[15px] w-[15px]" />
                )}
              </button>
            )}
          </RequireRole>
          <RequireRole min="manager">
            {card.archivedAt ? (
              // Archived: trash button = permanent delete (still gated by the
              // confirm dialog below).
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="flex h-9 w-9 items-center justify-center rounded-md bg-muted text-destructive hover:bg-muted/80"
                aria-label="Permanently delete card"
                title="Permanently delete"
              >
                <Trash2 className="h-[15px] w-[15px]" />
              </button>
            ) : (
              // Active: Archive button replaces Delete. Server refuses (409)
              // when the card is packaged into a product; the resolved error
              // surfaces in archiveError below.
              <button
                type="button"
                onClick={async () => {
                  setArchiveError(null);
                  const { error } = await archiveCard(card.id);
                  if (error) setArchiveError(error);
                }}
                className="flex h-9 w-9 items-center justify-center rounded-md bg-warning/15 text-warning hover:bg-warning/25"
                aria-label="Archive card"
                title="Archive"
              >
                <Archive className="h-[15px] w-[15px]" />
              </button>
            )}
          </RequireRole>
        </div>
      </div>

      {/* TNK-109: archive surface — error from the last archive attempt
          (typically a 409 "unpack first"), the Archived banner with Restore,
          or both. Rendered above the photos so it can't be missed. */}
      {archiveError && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="flex-1">{archiveError}</div>
          <button
            type="button"
            onClick={() => setArchiveError(null)}
            className="text-destructive hover:opacity-80"
            aria-label="Dismiss error"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {card.archivedAt && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm">
          <Archive className="h-4 w-4 shrink-0 text-warning" />
          <span className="flex-1 text-foreground">
            Archived on {new Date(card.archivedAt).toLocaleDateString()}
          </span>
          <RequireRole min="manager">
            <button
              type="button"
              onClick={async () => {
                setArchiveError(null);
                const { error } = await restoreCard(card.id);
                if (error) setArchiveError(error);
              }}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"
            >
              <ArchiveRestore className="h-3.5 w-3.5" />
              Restore
            </button>
          </RequireRole>
        </div>
      )}

      {/* ── Card photos (view mode) ────────────────────────────────────── */}
      {!editing && (card.imageFrontRef || card.imageBackRef) && (
        <div className="flex flex-wrap gap-3">
          {card.imageFrontRef && (
            <div className="space-y-1">
              <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Front
              </div>
              <CardImage
                imageRef={card.imageFrontRef}
                alt={`${card.name} front`}
                className="w-[180px]"
              />
            </div>
          )}
          {card.imageBackRef && (
            <div className="space-y-1">
              <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Back
              </div>
              <CardImage
                imageRef={card.imageBackRef}
                alt={`${card.name} back`}
                className="w-[180px]"
              />
            </div>
          )}
        </div>
      )}

      {/* ── Card attributes (artist, type, stage, year, finish, language, graded) ── */}
      {(() => {
        const rows: { label: string; value: string }[] = [];
        if (card.tcgArtist)
          rows.push({ label: "Illustrator", value: card.tcgArtist });
        if (card.tcgTypes && card.tcgTypes.length > 0)
          rows.push({ label: "Type", value: card.tcgTypes.join(", ") });
        if (card.tcgSupertype)
          rows.push({ label: "Supertype", value: card.tcgSupertype });
        if (card.tcgSubtypes && card.tcgSubtypes.length > 0)
          rows.push({ label: "Stage", value: card.tcgSubtypes.join(", ") });
        if (card.tcgHp) rows.push({ label: "HP", value: card.tcgHp });
        if (card.tcgSeries)
          rows.push({ label: "Series", value: card.tcgSeries });
        if (card.year) rows.push({ label: "Year", value: card.year });
        if (card.finish) rows.push({ label: "Finish", value: card.finish });
        if (card.language)
          rows.push({ label: "Language", value: card.language });
        if (card.graded) rows.push({ label: "Graded", value: "Yes" });
        if (rows.length === 0) return null;
        return (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-md border border-border bg-card p-3 text-[12px] sm:grid-cols-3">
            {rows.map(({ label, value }) => (
              <div key={label} className="min-w-0">
                <dt className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  {label}
                </dt>
                <dd className="truncate text-foreground">{value}</dd>
              </div>
            ))}
          </dl>
        );
      })()}

      {/* ── Delete confirmation ─────────────────────────────────────────── */}
      {/* TNK-109: the trash button only renders when the card is archived,
          so this confirmation is the permanent-delete path. Copy reflects
          that. */}
      {confirmDelete && (
        <div className="flex flex-wrap items-center gap-2 rounded-md bg-red-100 p-3">
          <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
          <span className="flex-1 text-sm font-semibold text-destructive">
            Permanently delete this card? This can't be undone.
          </span>
          <button
            type="button"
            onClick={() => setConfirmDelete(false)}
            className="rounded-md border border-destructive px-3.5 py-1.5 text-[13px] font-medium text-destructive hover:bg-destructive/10"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirmDelete}
            className="rounded-md bg-destructive px-3.5 py-1.5 text-[13px] font-bold text-white hover:opacity-90"
          >
            Permanently delete
          </button>
        </div>
      )}

      {/* ── Product nudge / eBay link ───────────────────────────────────── */}
      {!editing &&
        card.listingStatus === "Unlisted" &&
        (() => {
          const editableProducts = products.filter(
            (p) => p.status === "Draft" || p.status === "Ready to List",
          );
          const hasEditableProduct = editableProducts.length > 0;
          const handleClick = () => {
            if (hasEditableProduct) setProductPickerOpen(true);
            // TNK-113: forward this card as the seed so the new product
            // lands with it already in cardIds.
            else
              void navigate({
                to: "/products/create",
                search: { initialCardIds: card.id },
              });
          };
          return (
            <button
              type="button"
              onClick={handleClick}
              className="flex w-full items-center gap-3 rounded-md border border-border bg-card px-3.5 py-3 text-left hover:bg-card/80"
            >
              <Package className="h-[15px] w-[15px] text-secondary-foreground" />
              <div className="flex-1 space-y-0.5">
                <div className="text-[13px] font-semibold text-foreground">
                  {hasEditableProduct
                    ? "Add to a product to list on eBay"
                    : "Create a product to list on eBay"}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {hasEditableProduct
                    ? `Pick from ${editableProducts.length} draft / ready product${editableProducts.length === 1 ? "" : "s"}, or start a new one.`
                    : "No draft or ready-to-list products yet — start a new one."}
                </div>
              </div>
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          );
        })()}

      {addedToBanner && (
        <div className="flex items-center gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-600 dark:text-emerald-400">
          <Check className="h-3.5 w-3.5" />
          <span className="flex-1">{addedToBanner}</span>
        </div>
      )}

      <ProductPicker
        open={productPickerOpen}
        onClose={() => setProductPickerOpen(false)}
        products={products}
        onPick={(productId, productName) => {
          addCardsToProduct(productId, [card.id]);
          setProductPickerOpen(false);
          if (addedBannerTimer.current) clearTimeout(addedBannerTimer.current);
          setAddedToBanner(`Added to ${productName}`);
          addedBannerTimer.current = setTimeout(
            () => setAddedToBanner(null),
            5000,
          );
        }}
        onCreateNew={() => {
          setProductPickerOpen(false);
          // TNK-113: forward the seed card so "New product" from the
          // picker doesn't drop it.
          void navigate({
            to: "/products/create",
            search: { initialCardIds: card.id },
          });
        }}
      />

      {!editing && card.ebayListingId && (
        <button
          type="button"
          onClick={handleOpenEbay}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-amber-500 py-2.5 text-[13px] font-semibold text-amber-500 hover:bg-amber-500/10"
        >
          <ExternalLink className="h-[15px] w-[15px]" />
          View on eBay · #{card.ebayListingId}
        </button>
      )}

      {/* ── Price card (view mode) ──────────────────────────────────────── */}
      {!editing && (
        <div className="space-y-2.5 rounded-md bg-primary p-4">
          <div className="flex items-center justify-around">
            <div className="flex flex-1 flex-col items-center gap-0.5">
              <div className="text-xl font-bold text-emerald-200">
                ${card.expectedSalePrice.toFixed(2)}
              </div>
              <div className="text-[10px] text-primary-foreground/60">
                Sale Price
              </div>
            </div>
            <div className="h-9 w-px bg-primary-foreground/20" />
            <div className="flex flex-1 flex-col items-center gap-0.5">
              <div className="text-xl font-bold text-primary-foreground">
                ${card.costBasis.toFixed(2)}
              </div>
              <div className="text-[10px] text-primary-foreground/60">
                Cost Basis
              </div>
            </div>
            <div className="h-9 w-px bg-primary-foreground/20" />
            <div className="flex flex-1 flex-col items-center gap-0.5">
              <div
                className={`text-xl font-bold ${profit >= 0 ? "text-emerald-300" : "text-red-300"}`}
              >
                ${profit.toFixed(2)}
              </div>
              <div className="text-[10px] text-primary-foreground/60">
                Profit
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 text-xs text-primary-foreground/60">
              {roi.toFixed(1)}% ROI · TCG Avg: ${card.marketPrice.toFixed(2)}
              {card.quantity > 1 ? ` · Qty: ${card.quantity}` : ""}
            </div>
            <button
              type="button"
              onClick={handleRefreshPrice}
              disabled={refreshingPrice}
              className="inline-flex items-center gap-1 rounded bg-primary-foreground/10 px-2 py-1 text-[10px] font-medium text-primary-foreground/80 hover:bg-primary-foreground/20 disabled:opacity-50"
            >
              <RefreshCw
                className={`h-3 w-3 ${refreshingPrice ? "animate-spin" : ""}`}
              />
              {refreshingPrice
                ? "Fetching sales…"
                : card.pricedAt
                  ? `Refreshed ${formatTimeAgo(card.pricedAt)}`
                  : "Refresh Price"}
            </button>
          </div>
          {refreshNotice && (
            <div
              className={
                refreshNotice.kind === "success"
                  ? "mt-2 rounded bg-emerald-500/15 px-2 py-1 text-[11px] text-emerald-700 dark:text-emerald-300"
                  : refreshNotice.kind === "warning"
                    ? "mt-2 rounded bg-amber-500/15 px-2 py-1 text-[11px] text-amber-700 dark:text-amber-300"
                    : "mt-2 rounded bg-secondary/15 px-2 py-1 text-[11px] text-secondary"
              }
            >
              {refreshNotice.text}
            </div>
          )}
        </div>
      )}

      {/* ── Costing (view mode) ─────────────────────────────────────────── */}
      {!editing && (
        <div className="space-y-1.5 rounded-md bg-card p-4">
          <div className="mb-1 text-[10px] font-medium tracking-wider text-muted-foreground">
            COSTING
          </div>

          {viewLotStats ? (
            <>
              <div className="overflow-hidden rounded-md border border-primary/30 bg-primary/5">
                <div className="flex items-center gap-2 px-3 py-2.5">
                  <Package className="h-3.5 w-3.5 text-primary" />
                  <span className="text-xs font-semibold text-primary">
                    {viewLotStats.lot.seller || "Lot"} · Cost Breakdown
                  </span>
                </div>
                <div className="flex gap-3 px-3 pb-3">
                  <div className="flex-1 space-y-0.5">
                    <div className="text-[10px] font-medium tracking-wide text-muted-foreground">
                      Total Lot Cost
                    </div>
                    <div className="text-lg font-bold text-foreground">
                      ${viewLotStats.totalCost.toFixed(2)}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      purchase + shipping + tax
                    </div>
                  </div>
                  <div className="w-px self-stretch bg-border" />
                  <div className="flex-1 space-y-0.5">
                    <div className="text-[10px] font-medium tracking-wide text-muted-foreground">
                      Cards in Lot
                    </div>
                    <div className="text-lg font-bold text-foreground">
                      {viewLotStats.totalCards.toLocaleString()}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {viewLotStats.lot.sortingBins.reduce(
                        (s, b) => s + (b.count || 0),
                        0,
                      ) > 0
                        ? "from sorted bins"
                        : "estimated"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 border-t border-border px-3 py-2.5">
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-muted-foreground">
                      Suggested per-card cost
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      ${viewLotStats.totalCost.toFixed(2)} ÷{" "}
                      {viewLotStats.totalCards} cards
                    </div>
                  </div>
                  <div className="text-xl font-bold text-secondary-foreground">
                    ${viewLotStats.perCardCost.toFixed(4)}
                  </div>
                </div>
                {viewLotStats.singlesCount > 0 && (
                  <div className="border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
                    {viewLotStats.singlesCount} singles pulled · $
                    {viewLotStats.costAllocated.toFixed(2)} allocated so far
                  </div>
                )}
                <button
                  type="button"
                  onClick={() =>
                    navigate({
                      to: "/lots/$id",
                      params: { id: viewLotStats.lot.id },
                    })
                  }
                  className="flex w-full items-center gap-2 border-t border-border px-3 py-2.5 text-left hover:bg-muted/40"
                >
                  <div className="flex-1">
                    <div className="text-[11px] text-muted-foreground">
                      Source Lot
                    </div>
                    <div className="text-[13px] font-semibold text-primary">
                      {viewLotStats.lot.seller || "Unnamed lot"}
                      {viewLotStats.lot.purchaseDate
                        ? ` · ${new Date(
                            viewLotStats.lot.purchaseDate + "T12:00:00",
                          ).toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}`
                        : ""}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-primary" />
                </button>
              </div>

              <div className="space-y-1.5 pt-2">
                <CompareRow
                  label="This card's cost basis"
                  value={`$${card.costBasis.toFixed(4)}`}
                  valueClass="text-foreground"
                />
                <CompareRow
                  label="Lot suggested cost"
                  value={`$${viewLotStats.perCardCost.toFixed(4)}`}
                  valueClass="text-muted-foreground"
                />
                {costDiff != null && Math.abs(costDiff) > 0.0001 && (
                  <div
                    className={`flex items-center gap-2 rounded-md p-2.5 ${
                      Math.abs(costDiff) < 0.5
                        ? "bg-secondary/10"
                        : "bg-destructive/10"
                    }`}
                  >
                    {costDiff > 0 ? (
                      <ArrowUp className="h-3 w-3 text-destructive" />
                    ) : (
                      <ArrowDown className="h-3 w-3 text-emerald-500" />
                    )}
                    <span
                      className={`flex-1 text-xs ${
                        costDiff > 0 ? "text-destructive" : "text-emerald-500"
                      }`}
                    >
                      ${Math.abs(costDiff).toFixed(4)}{" "}
                      {costDiff > 0 ? "above" : "below"} lot average · Tap Edit
                      to adjust
                    </span>
                  </div>
                )}
                {costDiff != null && Math.abs(costDiff) <= 0.0001 && (
                  <div className="flex items-center gap-2 rounded-md bg-emerald-500/10 p-2.5">
                    <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                    <span className="text-xs text-emerald-500">
                      Cost basis matches lot average
                    </span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div>
              <CompareRow
                label="Cost Basis"
                value={
                  card.costBasis > 0 ? `$${card.costBasis.toFixed(2)}` : "—"
                }
                valueClass="text-foreground"
              />
              <CompareRow
                label="30-Day Avg Sold (TCG)"
                value={
                  card.marketPrice > 0 ? `$${card.marketPrice.toFixed(2)}` : "—"
                }
                valueClass="text-foreground"
                bordered
              />
              <CompareRow
                label="Expected Sale Price"
                value={
                  card.expectedSalePrice > 0
                    ? `$${card.expectedSalePrice.toFixed(2)}`
                    : "—"
                }
                valueClass="text-secondary-foreground"
                bordered
              />
              <div className="flex items-center justify-between border-t border-border py-2.5">
                <span className="text-[13px] text-muted-foreground">
                  Source Lot
                </span>
                {card.sourceLotId ? (
                  <span className="text-[13px] font-semibold text-muted-foreground">
                    Unknown lot
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={startEditing}
                    className="text-[13px] font-semibold text-secondary-foreground hover:underline"
                  >
                    Tap Edit to assign →
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Details (view mode) ─────────────────────────────────────────── */}
      {!editing && (
        <div className="space-y-0 rounded-md bg-card p-4">
          <div className="mb-1 text-[10px] font-medium tracking-wider text-muted-foreground">
            DETAILS
          </div>
          {[
            { label: "Variant", value: card.variant || "—" },
            { label: "Quantity", value: String(card.quantity) },
            { label: "Storage", value: card.storageLocation || "—" },
            { label: "Listing Status", value: card.listingStatus },
            { label: "Sale Venue", value: card.saleVenue || "—" },
          ].map((row, i) => (
            <CompareRow
              key={row.label}
              label={row.label}
              value={row.value}
              valueClass="text-foreground"
              bordered={i > 0}
            />
          ))}
        </div>
      )}

      {/* ── Price history (view mode) ───────────────────────────────────── */}
      {!editing && (
        <div className="space-y-2 rounded-md bg-card p-4">
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-medium tracking-wider text-muted-foreground">
              SALES HISTORY
            </div>
            <button
              type="button"
              onClick={() => {
                setShowLogPrice((v) => !v);
                setLogPriceError("");
              }}
              className="inline-flex items-center gap-1 rounded bg-secondary/20 px-2 py-1 text-[11px] font-semibold text-secondary-foreground hover:bg-secondary/30"
            >
              {showLogPrice ? (
                <X className="h-2.5 w-2.5" />
              ) : (
                <Plus className="h-2.5 w-2.5" />
              )}
              {showLogPrice ? "Cancel" : "Log sale"}
            </button>
          </div>

          {showLogPrice && (
            <div className="space-y-2.5 rounded-lg border border-border bg-muted/40 p-3">
              <div className="flex flex-wrap gap-1.5">
                {PRICE_SOURCES.map((src) => {
                  const active = logPriceSource === src;
                  return (
                    <button
                      key={src}
                      type="button"
                      onClick={() => setLogPriceSource(src)}
                      className={`rounded border px-2 py-1 text-[11px] font-medium ${
                        active
                          ? "border-secondary bg-secondary text-secondary-foreground"
                          : "border-border bg-muted text-muted-foreground"
                      }`}
                    >
                      {src}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <div
                  className={`flex flex-1 items-center gap-1.5 rounded-lg border bg-card px-2.5 py-2 ${
                    logPriceError ? "border-destructive" : "border-border"
                  }`}
                >
                  <span className="text-[13px] font-medium text-muted-foreground">
                    $
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={logPriceValue}
                    onChange={(e) => {
                      setLogPriceValue(e.target.value);
                      setLogPriceError("");
                    }}
                    placeholder="0.00"
                    className="flex-1 bg-transparent text-sm font-medium text-foreground placeholder:text-muted-foreground focus:outline-none"
                  />
                </div>
                <div className="flex flex-[1.2] items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-2">
                  <Calendar className="h-[11px] w-[11px] text-muted-foreground" />
                  <input
                    type="text"
                    value={logPriceDate}
                    onChange={(e) => {
                      setLogPriceDate(e.target.value);
                      setLogPriceError("");
                    }}
                    placeholder="Today"
                    className="flex-1 bg-transparent text-sm font-medium text-foreground placeholder:text-muted-foreground focus:outline-none"
                  />
                </div>
              </div>
              {logPriceError && (
                <p className="text-[11px] text-destructive">{logPriceError}</p>
              )}
              <button
                type="button"
                onClick={handleLogPrice}
                className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-secondary py-2.5 text-[13px] font-semibold text-secondary-foreground hover:opacity-90"
              >
                <Check className="h-3 w-3" />
                Save entry
              </button>
            </div>
          )}

          {card.priceHistory && card.priceHistory.length > 0 ? (
            <>
              {[...card.priceHistory].reverse().map((entry, i) => (
                <div
                  key={`${entry.recordedAt}-${i}`}
                  className={`flex items-center justify-between py-2 ${
                    i > 0 ? "border-t border-border" : ""
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <PriceSourceIcon source={entry.source} />
                    <div>
                      <div className="text-xs font-medium text-muted-foreground">
                        {new Date(entry.recordedAt).toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          },
                        )}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {entry.source}
                      </div>
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-foreground">
                    ${entry.price.toFixed(2)}
                  </div>
                </div>
              ))}
              <p className="mt-1 text-center text-[10px] text-muted-foreground">
                Up to last 20 entries · does not affect current market price
              </p>
            </>
          ) : (
            !showLogPrice && (
              <p className="text-[10px] text-muted-foreground">
                No sales reported yet · tap &quot;Log sale&quot; to add an entry
              </p>
            )
          )}
        </div>
      )}

      {card.notes && !editing && (
        <div className="space-y-1 rounded-md bg-muted p-3.5">
          <div className="text-[11px] font-medium tracking-wide text-muted-foreground">
            Notes
          </div>
          <p className="text-sm leading-5 text-foreground">{card.notes}</p>
        </div>
      )}

      {/* ── Status cycle (view mode) ────────────────────────────────────── */}
      {!editing && (
        <button
          type="button"
          onClick={cycleStatus}
          className="w-full rounded-md bg-secondary py-3.5 text-[15px] font-semibold text-secondary-foreground hover:opacity-90"
        >
          Mark as{" "}
          {card.listingStatus === "Unlisted"
            ? "Listed"
            : card.listingStatus === "Listed"
              ? "Sold"
              : "Unlisted"}
        </button>
      )}

      {/* ── Split stack (view mode) ─────────────────────────────────────── */}
      {!editing && card.quantity > 1 && !confirmSplit && (
        <button
          type="button"
          onClick={() => setConfirmSplit(true)}
          className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border py-3 text-[13px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Scissors className="h-4 w-4" />
          Split stack of {card.quantity}
        </button>
      )}
      {!editing && card.quantity > 1 && confirmSplit && (
        <div className="space-y-2 rounded-md bg-muted p-3">
          <div className="flex items-center gap-2">
            <Scissors className="h-4 w-4 shrink-0 text-foreground" />
            <span className="flex-1 text-sm text-foreground">
              Split into {card.quantity} separate records? Each can then carry
              its own photo, condition, location, cost basis, and status.
            </span>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirmSplit(false)}
              className="rounded-md border border-border px-3.5 py-1.5 text-[13px] font-medium text-foreground hover:bg-card"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                splitCardStack(card.id);
                navigate({ to: "/inventory" });
              }}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3.5 py-1.5 text-[13px] font-semibold text-primary-foreground hover:opacity-90"
            >
              <Scissors className="h-3.5 w-3.5" />
              Split into {card.quantity}
            </button>
          </div>
        </div>
      )}

      {/* ══════════════════ EDIT MODE ══════════════════════════════════════ */}
      {editing && (
        <>
          <div className="flex items-center gap-2 rounded-md bg-secondary/10 p-3">
            <Pencil className="h-3.5 w-3.5 text-secondary-foreground" />
            <span className="flex-1 text-[13px] font-medium text-secondary-foreground">
              Editing — tap Save to apply changes
            </span>
            <button
              type="button"
              onClick={cancelEditing}
              className="text-[13px] font-semibold text-secondary-foreground hover:underline"
            >
              Cancel
            </button>
          </div>

          {/* Card details */}
          <div className="space-y-1 rounded-md bg-card p-4">
            <div className="mb-1 text-[10px] font-medium tracking-wider text-muted-foreground">
              CARD DETAILS
            </div>
            <div ref={bindField("name")}>
              <FormField
                label="Card Name"
                value={name}
                onChangeText={(v) => {
                  setName(v);
                  if (v.trim()) setNameError("");
                }}
                placeholder="e.g. Charizard"
                required
                error={nameError}
              />
            </div>
            <DropdownField
              label="Game"
              value={game}
              options={GAMES}
              onChange={(v) => setGame(v as CardGame)}
            />
            {game === "Pokemon" ? (
              <SetCombobox
                value={cardSetName}
                onChange={setCardSetName}
                onSelectSet={(name, code) => {
                  setCardSetName(name);
                  if (code) setSetCode(code);
                }}
              />
            ) : (
              <FormField
                label="Set Name"
                value={cardSetName}
                onChangeText={setCardSetName}
                placeholder="e.g. Base Set"
              />
            )}
            <FormField
              label="Set Code"
              value={setCode}
              onChangeText={setSetCode}
              placeholder="e.g. BS1"
            />
            <FormField
              label="Card Number"
              value={cardNumber}
              onChangeText={setCardNumber}
              placeholder="e.g. 4/102"
            />
            <DropdownField
              label="Rarity"
              value={rarity}
              options={RARITIES}
              onChange={(v) => setRarity(v as CardRarity)}
            />
            <FormField
              label="Variant"
              value={variant}
              onChangeText={setVariant}
              placeholder="1st ed, staff, misprint…"
            />
            <div ref={bindField("condition")}>
              <DropdownField
                label="Condition"
                value={condition}
                options={CONDITIONS}
                onChange={(v) => {
                  setCondition(v as CardCondition);
                  setConditionError("");
                }}
                required
                error={conditionError}
              />
            </div>
            <div ref={bindField("quantity")}>
              <NumberField
                label="Quantity"
                value={quantity}
                onChangeText={(v) => {
                  setQuantity(v);
                  if (v.trim() && parseInt(v, 10) >= 1) setQuantityError("");
                }}
                placeholder="1"
                error={quantityError}
              />
            </div>
          </div>

          {/* Card attributes (eBay / pokemontcg.io enrichment) */}
          <div className="space-y-1 rounded-md bg-card p-4">
            <div className="mb-1 text-[10px] font-medium tracking-wider text-muted-foreground">
              CARD ATTRIBUTES
            </div>
            <FormField
              label="Illustrator"
              value={illustrator}
              onChangeText={setIllustrator}
              placeholder="e.g. Mina Nakai"
            />
            <DropdownField
              label="Energy Type"
              value={energyType}
              options={ENERGY_OPTIONS}
              onChange={setEnergyType}
            />
            <DropdownField
              label="Supertype"
              value={supertype}
              options={SUPERTYPE_OPTIONS}
              onChange={setSupertype}
            />
            <FormField
              label="Stage / Subtype"
              value={stage}
              onChangeText={setStage}
              placeholder="Basic, Stage 1, Supporter, Item, Stadium, Pokémon Tool…"
            />
            <FormField
              label="Year"
              value={year}
              onChangeText={setYear}
              placeholder="e.g. 2024"
              inputMode="numeric"
            />
            <DropdownField
              label="Finish"
              value={finish}
              options={FINISH_OPTIONS}
              onChange={(v) => setFinish(v as CardFinish | "")}
            />
            <DropdownField
              label="Language"
              value={language}
              options={LANGUAGES}
              onChange={setLanguage}
            />
            <DropdownField
              label="Grading"
              value={gradingCompany}
              options={GRADING_COMPANIES}
              onChange={setGradingCompany}
            />
          </div>

          {/* Photos */}
          <div className="space-y-3 rounded-md bg-card p-4">
            <div className="text-[10px] font-medium tracking-wider text-muted-foreground">
              PHOTOS
            </div>
            <div className="flex flex-wrap gap-4">
              <CardImageUploader
                label="Front"
                imageRef={imageFrontRef}
                onChange={setImageFrontRef}
              />
              <CardImageUploader
                label="Back"
                imageRef={imageBackRef}
                onChange={setImageBackRef}
              />
            </div>
          </div>

          {/* Costing & pricing */}
          <div className="space-y-1 rounded-md bg-card p-4">
            <div className="mb-1 text-[10px] font-medium tracking-wider text-muted-foreground">
              COSTING & PRICING
            </div>

            {lotOptions.length > 0 ? (
              <DropdownField
                label="Source Lot"
                value={sourceLotId}
                options={lotSelectOptions}
                onChange={(v) => setSourceLotId(v)}
              />
            ) : (
              <FormField
                label="Source Lot ID"
                value={sourceLotId}
                onChangeText={setSourceLotId}
                placeholder="Lot ID (optional)"
              />
            )}

            {editLotStats && (
              <div className="my-2 overflow-hidden rounded-md border border-primary/30 bg-primary/5">
                <div className="flex items-center gap-2 px-3 py-2.5">
                  <Package className="h-3.5 w-3.5 text-primary" />
                  <span className="text-xs font-semibold text-primary">
                    {editLotStats.lot.seller || "Lot"} · Cost Breakdown
                  </span>
                </div>
                <div className="flex gap-3 px-3 pb-3">
                  <div className="flex-1 space-y-0.5">
                    <div className="text-[10px] font-medium tracking-wide text-muted-foreground">
                      Total Lot Cost
                    </div>
                    <div className="text-lg font-bold text-foreground">
                      ${editLotStats.totalCost.toFixed(2)}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      purchase + shipping + tax
                    </div>
                  </div>
                  <div className="w-px self-stretch bg-border" />
                  <div className="flex-1 space-y-0.5">
                    <div className="text-[10px] font-medium tracking-wide text-muted-foreground">
                      Cards in Lot
                    </div>
                    <div className="text-lg font-bold text-foreground">
                      {editLotStats.totalCards.toLocaleString()}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {editLotStats.lot.sortingBins.reduce(
                        (s, b) => s + (b.count || 0),
                        0,
                      ) > 0
                        ? "from sorted bins"
                        : "estimated"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 border-t border-border px-3 py-2.5">
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-muted-foreground">
                      Per-card cost basis
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      ${editLotStats.totalCost.toFixed(2)} ÷{" "}
                      {editLotStats.totalCards} cards
                    </div>
                  </div>
                  <div className="text-xl font-bold text-secondary-foreground">
                    ${editLotStats.perCardCost.toFixed(4)}
                  </div>
                </div>
                {editLotStats.singlesCount > 0 && (
                  <div className="border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
                    {editLotStats.singlesCount} singles pulled · $
                    {editLotStats.costAllocated.toFixed(2)} allocated
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setCostBasis(editLotStats.perCardCost.toFixed(4));
                    setCostAutoFilled(true);
                  }}
                  className="flex w-full items-center gap-2 border-t border-border bg-secondary/10 px-3 py-2.5 text-left hover:bg-secondary/20"
                >
                  <RefreshCw className="h-3 w-3 text-secondary-foreground" />
                  <span className="text-xs font-semibold text-secondary-foreground">
                    Apply lot average (${editLotStats.perCardCost.toFixed(4)})
                  </span>
                </button>
              </div>
            )}

            <div ref={bindField("costBasis")}>
              <MoneyField
                label={
                  costAutoFilled
                    ? "Cost Basis (from lot average)"
                    : "Cost Basis"
                }
                value={costBasis}
                onChangeText={(v) => {
                  setCostBasis(v);
                  setCostAutoFilled(false);
                  if (
                    !v.trim() ||
                    (!isNaN(parseFloat(v)) && parseFloat(v) >= 0)
                  )
                    setCostBasisError("");
                }}
                error={costBasisError}
              />
            </div>
            <div ref={bindField("marketPrice")}>
              <MoneyField
                label="Market Price (NM)"
                value={marketPrice}
                onChangeText={(v) => {
                  setMarketPrice(v);
                  if (
                    !v.trim() ||
                    (!isNaN(parseFloat(v)) && parseFloat(v) >= 0)
                  )
                    setMarketPriceError("");
                }}
                error={marketPriceError}
              />
            </div>
            <div ref={bindField("expectedSalePrice")}>
              <MoneyField
                label="Expected Sale Price"
                value={expectedSalePrice}
                onChangeText={(v) => {
                  setExpectedSalePrice(v);
                  if (
                    !v.trim() ||
                    (!isNaN(parseFloat(v)) && parseFloat(v) >= 0)
                  )
                    setExpectedSalePriceError("");
                }}
                error={expectedSalePriceError}
              />
            </div>
          </div>

          {/* Storage & status */}
          <div className="space-y-3 rounded-md bg-card p-4">
            <div className="text-[10px] font-medium tracking-wider text-muted-foreground">
              STORAGE & STATUS
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Storage Bin
              </label>
              <button
                type="button"
                onClick={() => setBinPickerVisible(true)}
                className="flex w-full items-center gap-2 rounded-md bg-muted px-3 py-3 text-left"
              >
                <Inbox
                  className={`h-3.5 w-3.5 ${storageLocation ? "text-secondary-foreground" : "text-muted-foreground"}`}
                />
                <span
                  className={`flex-1 text-sm ${storageLocation ? "font-medium text-foreground" : "text-muted-foreground"}`}
                >
                  {storageLocation || "Tap to assign a bin…"}
                </span>
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </div>

            <DropdownField
              label="Listing Status"
              value={listingStatus}
              options={LISTING_STATUSES as unknown as string[]}
              onChange={(v) => setListingStatus(v as ListingStatus)}
            />
            <FormField
              label="Notes"
              value={notes}
              onChangeText={setNotes}
              placeholder="Any additional details…"
              multiline
            />
          </div>

          <div className="flex gap-2.5">
            <button
              type="button"
              onClick={cancelEditing}
              className="w-[90px] rounded-md border border-border py-3.5 text-sm font-medium text-muted-foreground hover:bg-muted"
            >
              Cancel
            </button>
            <div className="flex-1">
              <PrimaryButton
                label="Save Changes"
                onClick={handleSave}
                loading={saving}
              />
            </div>
          </div>
        </>
      )}

      <BinPickerModal
        open={binPickerVisible}
        value={storageLocation}
        onSelect={(n) => setStorageLocation(n)}
        onClose={() => setBinPickerVisible(false)}
      />
    </div>
  );
}

/**
 * Small per-source icon for the sales-history list. Lets the row be
 * scannable without having to read each source label.
 */
function PriceSourceIcon({ source }: { source: PriceSource }) {
  const cls = "h-3.5 w-3.5 shrink-0 text-muted-foreground";
  switch (source) {
    case "eBay Sold":
      return <ShoppingBag className={cls} aria-label="eBay sold" />;
    case "TCGplayer":
      return <TrendingUp className={cls} aria-label="TCGplayer" />;
    case "Manual":
      return <Pencil className={cls} aria-label="Manual entry" />;
    case "Other":
      return <Tag className={cls} aria-label="Other" />;
    default:
      return <Tag className={cls} />;
  }
}

function CompareRow({
  label,
  value,
  valueClass = "",
  bordered,
}: {
  label: string;
  value: string;
  valueClass?: string;
  bordered?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between py-2.5 ${
        bordered ? "border-t border-border" : ""
      }`}
    >
      <span className="text-[13px] text-muted-foreground">{label}</span>
      <span className={`text-[13px] font-semibold ${valueClass}`}>{value}</span>
    </div>
  );
}

interface ProductPickerProps {
  open: boolean;
  onClose: () => void;
  products: Product[];
  onPick: (productId: string, productName: string) => void;
  onCreateNew: () => void;
}

function ProductPicker({
  open,
  onClose,
  products,
  onPick,
  onCreateNew,
}: ProductPickerProps) {
  const editable = products
    .filter((p) => p.status === "Draft" || p.status === "Ready to List")
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  return (
    <ModalSheet
      open={open}
      onClose={onClose}
      ariaLabel="Add this card to a product"
    >
      <div className="px-4 pb-5 pt-1">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          Add this card to a product
        </h2>
        {editable.length === 0 ? (
          <p className="mb-3 text-xs text-muted-foreground">
            No draft or ready-to-list products yet. Start a new one below.
          </p>
        ) : (
          <div className="mb-3 max-h-[55vh] space-y-1.5 overflow-y-auto">
            {editable.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onPick(p.id, p.name || p.productType)}
                className="flex w-full items-center gap-3 rounded-md border border-border bg-card px-3 py-2.5 text-left hover:bg-card/80"
              >
                <Package className="h-4 w-4 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-foreground">
                    {p.name || p.productType}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {p.cardCount} card{p.cardCount === 1 ? "" : "s"} ·{" "}
                    {p.productType}
                  </div>
                </div>
                <Badge
                  label={p.status}
                  variant={productStatusBadge(p.status)}
                />
              </button>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={onCreateNew}
          className="flex w-full items-center gap-2 rounded-md border border-dashed border-secondary/50 bg-secondary/10 px-3 py-2.5 text-sm font-semibold text-secondary-foreground hover:bg-secondary/20"
        >
          <Plus className="h-4 w-4" />
          Create new product
        </button>
        <button
          type="button"
          onClick={onClose}
          className="mt-2 w-full rounded-md bg-muted px-3 py-2 text-xs font-semibold text-muted-foreground hover:bg-muted/80"
        >
          Cancel
        </button>
      </div>
    </ModalSheet>
  );
}
